import { Suspense, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";

import { Skeleton } from "@/components/_atoms/skeleton";
import { SkipLink } from "@/components/_atoms/skip-link";
import { NotFoundView } from "@/components/_molecules/not-found-view";
import { BasePageLayout } from "@/components/_templates/base-page-layout";
import { SidebarApp } from "@/components/_templates/sidebar-app/sidebar-app";
import { I18nProvider } from "@/domain/_shared/i18n/i18n-provider";
import { PreLoader } from "@/domain/_shared/preloader/components/preloader";
import { ClimateActionCreateHandlerProvider } from "@/domain/climate-action/hooks/use-create-climate-action-handler";
import { useDashboardExportSocket } from "@/domain/public-dashboard/hooks/use-dashboard-export-socket";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { useWorkspaceSettings } from "@/domain/workspace/hooks/api/use-workspace-settings";
import {
  CurrentWorkspaceContextProvider,
  useCurrentWorkspaceContext,
} from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { ChartThemeProvider } from "@/providers/ChartThemeProvider";
import { ChartPaletteConfig } from "@ark/shared";

const WorkspacePageSkeleton = () => (
  <div className="relative mx-10 mb-16 mt-0 flex grow items-stretch">
    <div className="box-border flex w-full touch-pan-x touch-pan-y flex-col">
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="my-6 w-full border-t border-solid" />
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  </div>
);

const InnerLayout = () => {
  const { currentUser } = useCurrentUserContext();
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { data: workspaceSettings } = useWorkspaceSettings({
    workspaceId: currentWorkspace.id,
  });

  useDashboardExportSocket();

  const initialConfig = useMemo(() => {
    if (workspaceSettings?.paletteConfig) {
      // paletteConfig comes as JSON string from GraphQL, parse it
      try {
        return typeof workspaceSettings.paletteConfig === "string"
          ? (JSON.parse(workspaceSettings.paletteConfig) as ChartPaletteConfig)
          : (workspaceSettings.paletteConfig as ChartPaletteConfig);
      } catch {
        return { default: "default" };
      }
    }
    if (workspaceSettings?.chartPaletteId) {
      return { default: workspaceSettings.chartPaletteId };
    }
    return { default: "default" };
  }, [workspaceSettings]);

  return (
    <>
      <SkipLink targetId="main-content" />
      <PreLoader workspaceId={currentWorkspace.id} userId={currentUser.id}>
        <I18nProvider
          workspaceId={currentWorkspace.id}
          userLocale={currentUser.locale}
          workspaceLocale={workspaceSettings?.locale}
        >
          <ChartThemeProvider
            initialPaletteId={workspaceSettings?.chartPaletteId ?? undefined}
            initialConfig={initialConfig}
          >
            <BasePageLayout
              renderSidebar={() => <SidebarApp workspace={currentWorkspace} user={currentUser} />}
              renderContent={() => (
                <ClimateActionCreateHandlerProvider>
                  <Suspense fallback={<WorkspacePageSkeleton />}>
                    <Outlet />
                  </Suspense>
                </ClimateActionCreateHandlerProvider>
              )}
            />
          </ChartThemeProvider>
        </I18nProvider>
      </PreLoader>
    </>
  );
};

export function WorkspaceLayout() {
  const { workspaceUrl } = useParams();
  const { currentUser } = useCurrentUserContext();

  if (!workspaceUrl) {
    return <NotFoundView />;
  }

  return (
    <CurrentWorkspaceContextProvider workspaceUrl={workspaceUrl} userId={currentUser.id}>
      <InnerLayout />
    </CurrentWorkspaceContextProvider>
  );
}
